import axios from 'axios';
import store from '../store';
import { setError, clearError } from '../store/slices/errorSlice';
import { expireSession, isSessionExpiredError } from '@/utils/session';

/**
 * Simple API Configuration
 * All API URLs and axios calls in one file
 * Base URL comes from .env file (VITE_API_BASE_URL)
 */

// Get base URL from environment variable
const baseURL =
  import.meta.env.VITE_API_BASE_URL || 'https://stage.bizz.plus/api';

// Create axios instance
const api = axios.create({
  baseURL: baseURL,
  timeout: 1000000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests automatically
api.interceptors.request.use(
  config => {
    // Support the project's expected token keys. Auth slices set "auth_token" (and sometimes "superAdminToken").
    const token =
      localStorage.getItem('auth_token') ||
      localStorage.getItem('authToken') ||
      localStorage.getItem('superAdminToken') ||
      null;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

// Handle errors
api.interceptors.response.use(
  response => {
    store.dispatch(clearError());
    return response;
  },
  error => {
    // Aborted/canceled requests are intentional — do not toast as errors
    if (
      error?.code === 'ERR_CANCELED' ||
      error?.name === 'CanceledError' ||
      error?.name === 'AbortError'
    ) {
      return Promise.reject(error);
    }
    // Handle 401 - redirect to login
    if (isSessionExpiredError(error)) {
      expireSession();
      return Promise.reject(error);
    }
    const message = error.response?.data?.message || error.message || "Something went wrong";
    store.dispatch(setError(message));
    return Promise.reject(error);
  }
);

/**
 * ============================================
 * API ENDPOINTS - All URLs in one place
 * ============================================
 */
const endpoints = {
  // Authentication
  login: '/superadmin/login',
  users: '/superadmin/users',
  companies: '/companies',
  companiesFilter: '/companies/filter',
  companyBySlug: '/companies/slug',
  companyById: '/companies',
  setupCompanyProfile: '/companies/profile/setup',
  handlesSuggest: '/handles/suggest',
  handlesCheck: '/handles/check',
  industries: '/industries/active',
  categories: '/categories/active',
  cities: '/location/cities',
  saveLocation: '/location/save-with-create',
  products: '/products',

  sendOtp: '/auth/send-otp',
  verifyOtp: '/auth/verify-otp',
  resendOtp: '/auth/resend-otp',
  getCurrentUser: '/auth/profile',

  createCompany: '/companies',
  updateCompany: '/companies',
  getCompany: '/companies',
  dmsCompany: '/dms/company',

  activeRoles: '/roles/active',
  selectRole: '/auth/select-role',
  companyRoles: '/company-roles',
  activeCompanyRoles: '/company-roles/active',
  permissionPages: '/permission-pages',
  companyRolePermissions: '/company-role-permissions',

  conversations: '/messages',
  conversationMeta: '/messages/meta',
  olderConversation: '/messages/older',
  uploadFile: '/files/upload',
  recentConversations: '/messages/recent',


  connectionRequests: '/requests',
  receivedRequests: '/requests',
  sentRequests: '/requests',
  generalConnections: '/requests',
  acceptRequest: '/requests',
  rejectRequest: '/requests',
  myConnections: '/requests/connections',


  allUsers: '/users',
  myCompany: '/companies',
  requirements: '/requirements',
  createRequirement: '/requirements/create',
  updateRequirement: '/requirements',
  deleteRequirement: '/requirements',
  productBySlug: '/products/slug',
  productById: '/products',
  productsByCompany: '/products/company',
  createProduct: '/products/create',
  updateProduct: '/products',
  deleteProduct: '/products',
  deleteProductImage: '/products/images',

  notifications: '/notifications',

  // AI Chatbot (Bizz+ assistant)
  chatbotMessage: '/chatbot/message',
  chatbotConversations: '/chatbot/conversations',

  saveForLater: '/companies',
  removeFromSaved: '/companies',
  getSavedForLater: '/companies/saved-for-later',
  contacts: '/contacts',

  // Mapping Requests
  mappingRequests: '/mapping-requests',
  listMappingRequests: '/mapping-requests/list-requests',
  getMappingRequest: '/mapping-requests/get-request',
  getMappingRequestByManuDistriMappingId: '/mapping-requests/by-manu-distri-mapping',
  createMappingRequest: '/mapping-requests/create-request',
  acceptMappingRequest: '/mapping-requests',
  rejectMappingRequest: '/mapping-requests',
  mappingRequestSkus: '/mapping-requests',
  productAnalytics: '/mapping-requests/product-analytics',

  // Mapping Stock Items
  mappingStockItems: '/mapping-stock-items',

  salesTeamMapping: '/sales-team-mapping',
  hierarchyRemainingStockValue: '/hierarchy-mapping/remaining-stock-value',

  sales: '/dms/transactions/sales',
  purchase: '/dms/transactions/purchase',
  payables: '/dms/transactions/payables',
  receivable: '/dms/transactions/receivable',
  creditNotes: '/dms/transactions/credit-notes',
  debitNotes: '/dms/transactions/debit-notes',
  generals: '/dms/transactions/generals',
  invoices: '/dms/invoices',
  invoiceDetail: '/dms/invoices',
  filterOptions: '/dms/dashboard/filter-options',

  skus: '/dms/skus',
  skusDualContext: '/dms/skus/dual-context',
  skusFindPage: '/dms/skus/find-page',
  skuRemainingStockValue: '/dms/skus/remaining-stock-value',
  dmsManufacturers: '/dms/manufacturers',

  // User Management
  companyUsers: '/users/company',
  createCompanyUser: '/users/company-user',
  companiesUser: '/companies-user',
  // Receivables
  agingBuckets: '/dms/aging/buckets',
  agingCustomerBuckets: '/dms/aging/customer-buckets',
  agingCustomerDetail: '/dms/aging/customer-detail',
  // Payables aging (Purchases - Payments - Debit notes)
  payablesAgingBuckets: '/dms/payables-aging/buckets',
  payablesAgingSupplierBuckets: '/dms/payables-aging/supplier-buckets',
  payablesAgingSupplierDetail: '/dms/payables-aging/supplier-detail',
  activeCategories: '/bizz-industry-categories/active',
  activeSubcategories: '/bizz-industry-subcategories/active',

  onboardingState: '/companies',
  //Company Selection
  selectCompany: "/auth/select-company",
};

/**
 * ============================================
 * API FUNCTIONS - All axios calls
 * ============================================
 */

// Login
export const login = async (email, password) => {
  const response = await api.post(endpoints.login, { email, password });
  return response.data;
};

export const fetchUsers = async () => {
  const response = await api.get(endpoints.login);
  return response.data;
};

/**
 * ============================================
 * EXPORTS
 * ============================================
 */

// Export axios instance
export default api;

// Export endpoints object (for reference)
export { endpoints };
